import { Component, OnDestroy, OnInit, AfterViewInit } from "@angular/core";
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { ProjectTable } from "src/app/components/project-table/project-table.component";
import {
  ProjectStatus,
  ProjectStatusFilter,
  PsaProjectData,
  PsaProjectFilter,
  PsaProjectsResponse,
  getProjectStatusDisplayLabel,
  getProjectStatusIcon,
} from "src/app/shared/interfaces/psa-project-register";
import { ProjectService } from "../../services/project.service";
import { catchError, map, Observable, Subscription } from "rxjs";
import { DatePipe } from "@angular/common";
import { Router } from "@angular/router";

type ProjectSortColumn = 'insertDate' | 'status';
type SortDirection = 'asc' | 'desc';

@Component({
  standalone: false,
  selector: 'app-project-consult',
  templateUrl: './project-consult.component.html',
  styleUrls: ['./project-consult.component.scss']
})
export class ProjectConsultComponent extends ProjectTable<PsaProjectData> implements OnInit, AfterViewInit, OnDestroy {
  selectedStatus: B3SelectItem[] = [];
  statusSearchMapped: Record<string, string> = {
    'Todos': 'Todos',
    'Pendente de verificação do auditor': 'PROJECT_PENDING_AUDITOR_VALIDATION',
    'Rejeitado pelo auditor': 'PROJECT_REJECTED_BY_AUDITOR',
    'Pendente de confirmação do desenvolvedor': 'PROJECT_PENDING_DEVELOPER_CONFIRMATION',
    'Rejeitado pelo desenvolvedor': 'PROJECT_REJECTED_BY_DEVELOPER',
    'Registrado na B3': 'PROJECT_REGISTERED_IN_B3',
    'Rascunho': 'PROJECT_DRAFT',
  };

  searchTerm = '';
  searchStatus: B3SelectItem = this.selectedStatus[0];

  isDetailsDrawerOpen = false;
  selectedProjectId: number | null = null;
  activeToast: {
    open: boolean;
    variant: 'success' | 'danger';
    label: string;
    description: string;
  } | null = null;

  private loadSubscription?: Subscription;
  private filterEventsEnabled = false;
  private insertDateSortKeys = new WeakMap<PsaProjectData, string>();

  constructor(
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly projectService: ProjectService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadSelectStatus();
    this.resetSearchFields();
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.filterEventsEnabled = true;
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get projectsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    return count === 1 ? '1 projeto encontrado' : `${count} projetos encontrados`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? 10;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  resetSearchFields(): void {
    this.searchTerm = '';
    this.searchStatus = this.selectedStatus[0];
    this.pagination.page = 1;
  }

  loadSelectStatus() {
    Object.keys(ProjectStatusFilter).forEach((key, index) => {
      if (isNaN(Number(key))) {
        const value = ProjectStatusFilter[key as keyof typeof ProjectStatusFilter];
        this.selectedStatus.push({ inputId: (index + 1), label: value });
      }
    });
  }

  loadDataAndUpdateTable(psaProjectFilter: PsaProjectFilter) {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.loadProjects(psaProjectFilter).subscribe(projects => {
      if (!projects) {
        return;
      }

      this.itens = this.processData(projects.data?.carbonProjects ?? []);
      this.totalItens = projects.meta?.totalRecords ?? 0;
      this.totalPages = projects.meta?.totalPages
        ?? Math.ceil(this.totalItens / (this.pagination.size ?? 10));
    });
  }

  cleanFilters(): void {
    this.resetSearchFields();
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  loadProjects(psaProjectFilter: PsaProjectFilter): Observable<PsaProjectsResponse | undefined> {
    return this.projectService.getProjectsConsult(psaProjectFilter, this.pagination).pipe(
      map((res: PsaProjectsResponse) => res || undefined),
      catchError(error => {
        console.error("Erro ao carregar os projetos. Stack error: ", error);
        return [undefined];
      })
    );
  }

  processData(allProjects: PsaProjectData[]): PsaProjectData[] {
    return allProjects.map(project => {
      const insertDateSortKey = project.insertDate ?? '';
      const row: PsaProjectData = {
        ...project,
        insertDate: this.formatInsertDate(insertDateSortKey),
        status: project.isDraft || project.status === 'PROJECT_DRAFT'
          ? ProjectStatus.PROJECT_DRAFT
          : this.mapProjectStatus(project.status),
      };

      this.insertDateSortKeys.set(row, insertDateSortKey);
      return row;
    });
  }

  navigateToProjectRegister() {
    this.router.navigate(['/registro-projeto']);
  }

  onFilterTable(): void {
    if (!this.filterEventsEnabled) {
      return;
    }

    this.pagination.page = 1;
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onProjectAction(project: PsaProjectData): void {
    if (project.isDraft) {
      this.continueDraftRegistration(project);
      return;
    }

    this.openProjectDetails(project);
  }

  openProjectDetails(project: PsaProjectData): void {
    if (project.isDraft) {
      return;
    }

    const projectId = project.projectId ?? null;

    if (projectId == null) {
      this.isDetailsDrawerOpen = false;
      this.selectedProjectId = null;
      return;
    }

    if (this.isDetailsDrawerOpen && this.selectedProjectId === projectId) {
      this.isDetailsDrawerOpen = false;
      this.selectedProjectId = null;
      queueMicrotask(() => {
        this.selectedProjectId = projectId;
        this.isDetailsDrawerOpen = true;
      });
      return;
    }

    this.selectedProjectId = projectId;
    this.isDetailsDrawerOpen = true;
  }

  onDetailsDrawerOpenChange(open: boolean): void {
    this.isDetailsDrawerOpen = open;
    if (!open) {
      this.selectedProjectId = null;
    }
  }

  onProjectUpdatedInDrawer(): void {
    this.loadDataAndUpdateTable(this.buildCurrentFilter());
  }

  onRmConfirmed(event: { projectId: number; projectName: string }): void {
    this.activeToast = {
      open: true,
      variant: 'success',
      label: 'Projeto recebido e registrado na B3!',
      description: event.projectName || '-',
    };
    this.onProjectUpdatedInDrawer();
  }

  onDetailsExported(): void {
    this.activeToast = {
      open: true,
      variant: 'success',
      label: 'Arquivo com detalhes exportado com sucesso!',
      description: '',
    };
  }

  onToastDismiss(): void {
    this.activeToast = null;
  }

  continueDraftRegistration(project: PsaProjectData): void {
    if (project.projectId == null) {
      return;
    }

    this.router.navigate(['/registro-projeto', project.projectId]);
  }

  isDraftProject(project: PsaProjectData): boolean {
    return project.isDraft === true;
  }

  getProjectActionIcon(project: PsaProjectData): string {
    return project.isDraft ? 'open_in_new' : 'visibility';
  }

  getProjectActionLabel(project: PsaProjectData): string {
    return project.isDraft
      ? `Continuar cadastro do projeto ${project.projectName || ''}`
      : `Visualizar detalhes do projeto ${project.projectName || ''}`;
  }

  getStatusLabel(status?: string): string {
    return getProjectStatusDisplayLabel(status);
  }

  getStatusIcon(status?: string): string {
    return getProjectStatusIcon(status);
  }

  getStatusTone(status?: string): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    switch (status) {
      case ProjectStatus.PROJECT_REJECTED_BY_AUDITOR:
      case ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER:
        return 'danger';
      case ProjectStatus.PROJECT_APPROVED_BY_AUDITOR:
      case ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER:
      case ProjectStatus.PROJECT_REGISTERED_IN_B3:
        return 'success';
      case ProjectStatus.PROJECT_DRAFT:
      case ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION:
        return 'warning';
      case ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION:
        return 'info';
      default:
        return 'info';
    }
  }

  onTableSort(event: Event): void {
    const customEvent = event as CustomEvent<{ column: number; direction: SortDirection }>;
    const columnMap: Record<number, ProjectSortColumn> = {
      1: 'insertDate',
      2: 'status',
    };
    const column = columnMap[customEvent.detail?.column];

    if (!column) {
      return;
    }

    let direction = customEvent.detail.direction;

    if (this.isAlreadySorted(column, direction)) {
      direction = direction === 'asc' ? 'desc' : 'asc';
    }

    this.sortProjects(this.itens, column, direction);
  }

  trackByProjectId(_index: number, project: PsaProjectData): string | number {
    const prefix = project.isDraft ? 'draft' : 'project';
    return `${prefix}-${project.projectId ?? _index}`;
  }

  private buildCurrentFilter(): PsaProjectFilter {
    return {
      projectName: this.searchTerm || '',
      status: this.searchStatus.label !== 'Todos'
        ? this.statusSearchMapped[this.searchStatus.label || '']
        : '',
    };
  }

  private formatInsertDate(value?: string | null): string {
    if (!value?.trim()) {
      return '-';
    }

    const trimmed = value.trim();
    const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day}/${month}/${year}`;
    }

    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime())) {
      return trimmed;
    }

    return this.datePipe.transform(parsed, 'dd/MM/yyyy') ?? trimmed;
  }

  private mapProjectStatus(status?: string): string {
    switch (status) {
      case 'PROJECT_PENDING_AUDITOR_VALIDATION':
        return ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION;
      case 'PROJECT_APPROVED_BY_AUDITOR':
        return ProjectStatus.PROJECT_APPROVED_BY_AUDITOR;
      case 'PROJECT_REJECTED_BY_AUDITOR':
        return ProjectStatus.PROJECT_REJECTED_BY_AUDITOR;
      case 'PROJECT_ADJUSTED_BY_DEVELOPER':
        return ProjectStatus.PROJECT_ADJUSTED_BY_DEVELOPER;
      case 'PROJECT_PENDING_DEVELOPER_CONFIRMATION':
        return ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION;
      case 'PROJECT_CONFIRMED_BY_DEVELOPER':
        return ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER;
      case 'PROJECT_REJECTED_BY_DEVELOPER':
        return ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER;
      case 'PROJECT_REGISTERED_IN_B3':
        return ProjectStatus.PROJECT_REGISTERED_IN_B3;
      case 'PROJECT_DRAFT':
        return ProjectStatus.PROJECT_DRAFT;
      default:
        return status ?? '';
    }
  }

  private sortProjects(
    projects: PsaProjectData[],
    column: ProjectSortColumn,
    direction: SortDirection,
  ): PsaProjectData[] {
    const factor = direction === 'asc' ? 1 : -1;

    return projects.sort((left, right) => {
      const leftValue = this.getSortValue(left, column);
      const rightValue = this.getSortValue(right, column);
      return leftValue.localeCompare(rightValue, 'pt-BR', { sensitivity: 'base' }) * factor;
    });
  }

  private isAlreadySorted(column: ProjectSortColumn, direction: SortDirection): boolean {
    for (let index = 0; index < this.itens.length - 1; index++) {
      const leftValue = this.getSortValue(this.itens[index], column);
      const rightValue = this.getSortValue(this.itens[index + 1], column);
      const comparison = leftValue.localeCompare(rightValue, 'pt-BR', { sensitivity: 'base' });

      if (direction === 'asc' && comparison > 0) {
        return false;
      }

      if (direction === 'desc' && comparison < 0) {
        return false;
      }
    }

    return true;
  }

  private getSortValue(project: PsaProjectData, column: ProjectSortColumn): string {
    switch (column) {
      case 'status':
        return getProjectStatusDisplayLabel(project.status);
      case 'insertDate':
      default:
        return this.insertDateSortKeys.get(project) ?? project.insertDate ?? '';
    }
  }
}
